import axios from 'axios'
import { getApiBaseUrl } from '../utils/apiConfig'

const API_BASE_URL = getApiBaseUrl()

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
})

const redirectToLogin = () => {
  if (typeof window === 'undefined') return
  if (window.location.pathname === '/login') return

  // Keep navigation in SPA history to avoid host 404 on deep-link reloads.
  window.history.replaceState({}, '', '/login')
  window.dispatchEvent(new PopStateEvent('popstate'))
}

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
}, (error) => {
  return Promise.reject(error)
})

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const errorCode = error.response?.data?.code
      const message = error.response?.data?.message || 'Session expired'

      // Store the logout reason for display
      if (errorCode) {
        sessionStorage.setItem('logoutReason', message)
        sessionStorage.setItem('logoutCode', errorCode)
      }

      localStorage.removeItem('token')
      localStorage.removeItem('user')
      redirectToLogin()
    }
    return Promise.reject(error)
  }
)

export const authService = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
}

export const userService = {
  getAllUsers: () => api.get('/users/users'),
  createUser: (data) => api.post('/users/users', data),
  updateUser: (id, data) => api.put(`/users/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/users/${id}`)
}

export const projectService = {
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getDetailItems: (projectId) => api.get(`/projects/${projectId}/detail-items`),
  addDetailItem: (projectId, data) => api.post(`/projects/${projectId}/detail-items`, data),
  updateDetailItem: (projectId, itemId, data) => api.put(`/projects/${projectId}/detail-items/${itemId}`, data),
  deleteDetailItem: (projectId, itemId) => api.delete(`/projects/${projectId}/detail-items/${itemId}`),
}

export const invoiceService = {
  getAll: () => api.get('/invoices'),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  generatePDF: (id) => api.post(`/invoices/${id}/generate-pdf`),
  uploadInvoicePdf: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/invoices/${id}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadPaymentEvidence: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/invoices/${id}/upload-payment-evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadDocument: (id, formData) => {
    return api.post(`/invoices/${id}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
}

export const expenseService = {
  getAll: () => api.get('/expenses'),
  getById: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  updateWithAttachments: (id, formData, expenseType = 'Receipt') => {
    const category = String(expenseType || 'Receipt').toLowerCase() === 'advanced' ? 'advanced' :
                    (String(expenseType || 'Receipt').toLowerCase() === 'direct' ? 'direct' : 'receipt')
    return api.put(`/expenses/${id}/upload/${category}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  delete: (id) => api.delete(`/expenses/${id}`),
  approve: (id) => api.post(`/expenses/${id}/approve`),
  reject: (id) => api.post(`/expenses/${id}/reject`),
  uploadAttachments: (formData, expenseType = 'Receipt') => {
    const category = String(expenseType || 'Receipt').toLowerCase() === 'advanced' ? 'advanced' :
                    (String(expenseType || 'Receipt').toLowerCase() === 'direct' ? 'direct' : 'receipt')
    return api.post(`/expenses/upload-attachments/${category}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
}

export const employeeService = {
  getAll: () => api.get('/employees'),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
}

export const clientService = {
  getAll: () => api.get('/clients'),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
}

export const salaryService = {
  getAll: () => api.get('/salaries'),
  getSummary: (params = {}) => api.get('/salaries/summary', { params }),
  upsertSummaryMeta: (data) => api.put('/salaries/summary/meta', data),
  getById: (id) => api.get(`/salaries/${id}`),
  create: (data) => api.post('/salaries', data),
  update: (id, data) => api.put(`/salaries/${id}`, data),
  delete: (id) => api.delete(`/salaries/${id}`),
  approve: (id) => api.post(`/salaries/${id}/approve`),
}

export const attendanceService = {
  getAll: () => api.get('/attendance'),
  getById: (id) => api.get(`/attendance/${id}`),
  create: (data) => api.post('/attendance', data),
  update: (id, data) => api.put(`/attendance/${id}`, data),
  delete: (id) => api.delete(`/attendance/${id}`),
}

export const quotationService = {
  getAll: () => api.get('/quotations'),
  getByProject: (projectId) => api.get(`/quotations/project/${projectId}`),
  getById: (id) => api.get(`/quotations/${id}`),
  create: (data) => api.post('/quotations', data),
  createWithFile: (formData) => api.post('/quotations/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  delete: (id) => api.delete(`/quotations/${id}`),
  generatePDF: (id) => api.post(`/quotations/${id}/generate-pdf`),
  getDetailItems: (quotationId) => api.get(`/quotations/${quotationId}/detail-items`),
  addDetailItem: (quotationId, data) => api.post(`/quotations/${quotationId}/detail-items`, data),
  updateDetailItem: (quotationId, itemId, data) => api.put(`/quotations/${quotationId}/detail-items/${itemId}`, data),
  deleteDetailItem: (quotationId, itemId) => api.delete(`/quotations/${quotationId}/detail-items/${itemId}`),
}

export const purchaseOrderService = {
  getAll: () => api.get('/purchase-orders'),
  getByProject: (projectId) => api.get(`/purchase-orders/project/${projectId}`),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  generatePDF: (id) => api.post(`/purchase-orders/${id}/generate-pdf`),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  createWithFile: (formData) => api.post('/purchase-orders/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateWithFile: (id, formData) => api.put(`/purchase-orders/${id}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
}

export const creditNoteService = {
  getAll: () => api.get('/credit-notes'),
  getById: (id) => api.get(`/credit-notes/${id}`),
  create: (data) => api.post('/credit-notes', data),
  update: (id, data) => api.put(`/credit-notes/${id}`, data),
  delete: (id) => api.delete(`/credit-notes/${id}`)
}

export const settingsService = {
  getMobileBlockView: () => api.get('/settings/mobile-block-view'),
  updateMobileBlockView: (enabled) => api.put('/settings/mobile-block-view', { enabled }),
  getPublicFormLimitation: () => api.get('/settings/public-form-limitation'),
  updatePublicFormLimitation: (enabled) => api.put('/settings/public-form-limitation', { enabled })
}

export const taskAssignmentService = {
  getAll: (params = {}) => api.get('/task-assignments', { params }),
  getById: (id) => api.get(`/task-assignments/${id}`),
  create: (data) => api.post('/task-assignments', data),
  update: (id, data) => api.put(`/task-assignments/${id}`, data),
  delete: (id) => api.delete(`/task-assignments/${id}`)
}

export const assetService = {
  getAll: (params = {}) => api.get('/assets', { params }),
  getById: (id) => api.get(`/assets/${id}`),
  getSummary: () => api.get('/assets/summary'),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id) => api.delete(`/assets/${id}`),
  getTransactions: (id) => api.get(`/assets/${id}/transactions`),
  recordDepreciation: (id, data) => api.post(`/assets/${id}/depreciate`, data)
}

export const liabilityService = {
  getAll: (params = {}) => api.get('/liabilities', { params }),
  getById: (id) => api.get(`/liabilities/${id}`),
  getSummary: () => api.get('/liabilities/summary'),
  create: (data) => api.post('/liabilities', data),
  update: (id, data) => api.put(`/liabilities/${id}`, data),
  delete: (id) => api.delete(`/liabilities/${id}`),
  getPayments: (id) => api.get(`/liabilities/${id}/payments`),
  recordPayment: (id, data) => api.post(`/liabilities/${id}/payment`, data)
}

export const financialReportService = {
  getBalanceSheet: (params = {}) => api.get('/reports/balance-sheet', { params }),
  getIncomeStatement: (params = {}) => api.get('/reports/income-statement', { params }),
  getCashFlow: (params = {}) => api.get('/reports/cash-flow', { params }),
  getFinancialRatios: (params = {}) => api.get('/reports/financial-ratios', { params }),
  getTrialBalance: (params = {}) => api.get('/reports/trial-balance', { params })
}

export default api
